import { Form, Input, InputNumber, Select, Row, Col, Divider, Button, Card, Space, Empty } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  EMPTY_RATE_SCENARIO,
  type StructuredData,
  type RateScenario,
  type RateAssumptions,
  type ProgramEligibility,
} from '@/types/topic';
import { useInvestorNames } from '@/hooks/useInvestors';
import { OrderedListEditor } from './OrderedListEditor';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'HI', 'IA', 'ID', 'IL', 'IN',
  'KS', 'KY', 'LA', 'MA', 'MD', 'ME', 'MI', 'MN', 'MO', 'MS', 'MT', 'NC', 'ND', 'NE', 'NH', 'NJ',
  'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VA', 'VT', 'WA',
  'WI', 'WV', 'WY',
];

const full = { width: '100%' } as const;
const usd = {
  formatter: (n: number | undefined) =>
    n !== undefined && n !== null ? `$ ${n}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '',
  parser: (s: string | undefined) => { const c = (s ?? '').replace(/[^\d.]/g, ''); return c ? Number(c) : 0; },
};

interface Props {
  value: StructuredData;
  onChange: (next: StructuredData) => void;
}

/** Program structured editor: top-level identity, a repeatable list of rate
 *  scenarios, the eligibility group, and highlight/feature/notes lists. */
export function MortgageForm({ value, onChange }: Props) {
  const investorNames = useInvestorNames();
  const set = <K extends keyof StructuredData>(k: K, v: StructuredData[K]) => onChange({ ...value, [k]: v });
  const setElig = <K extends keyof ProgramEligibility>(k: K, v: ProgramEligibility[K]) =>
    onChange({ ...value, eligibility: { ...value.eligibility, [k]: v } });

  const scenarios = value.rate_scenarios ?? [];
  const setScenario = (i: number, next: RateScenario) =>
    set('rate_scenarios', scenarios.map((s, j) => (j === i ? next : s)));
  const addScenario = () => set('rate_scenarios', [...scenarios, { ...EMPTY_RATE_SCENARIO, assumptions: { ...EMPTY_RATE_SCENARIO.assumptions } }]);
  const removeScenario = (i: number) => set('rate_scenarios', scenarios.filter((_, j) => j !== i));

  const e = value.eligibility;

  return (
    <Form layout="vertical">
      {/* Program identity */}
      <Divider orientation="left" style={{ marginTop: 0 }}>Program</Divider>
      <Row gutter={12}>
        <Col xs={24} md={8}>
          <Form.Item label="Investor" style={{ marginBottom: 12 }}>
            <Select
              value={value.investor ?? undefined}
              onChange={(v) => set('investor', v ?? null)}
              options={investorNames.map((n) => ({ value: n, label: n }))}
              placeholder="Select investor"
              allowClear showSearch
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Program name" style={{ marginBottom: 12 }}>
            <Input value={value.program_name ?? ''} onChange={(ev) => set('program_name', ev.target.value || null)} placeholder="e.g. PRIME JUMBO (THUNDER)" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Promo tag" style={{ marginBottom: 12 }}>
            <Input value={value.promo_tag ?? ''} onChange={(ev) => set('promo_tag', ev.target.value || null)} placeholder="e.g. TO $3 MILLION" />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item label="Loan category" style={{ marginBottom: 12 }}>
        <Input value={value.loan_category ?? ''} onChange={(ev) => set('loan_category', ev.target.value || null)} placeholder="e.g. Prime Jumbo, DSCR, CRA" />
      </Form.Item>

      {/* Rate scenarios — repeatable */}
      <Divider orientation="left">
        Rate scenarios{scenarios.length ? ` (${scenarios.length})` : ''}
      </Divider>
      {scenarios.length === 0 && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No rate scenarios (feature/grant flyers often have none)" style={{ marginBottom: 12 }} />
      )}
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {scenarios.map((s, i) => (
          <ScenarioCard key={i} index={i} value={s} onChange={(n) => setScenario(i, n)} onRemove={() => removeScenario(i)} />
        ))}
      </Space>
      <Button icon={<PlusOutlined />} onClick={addScenario} style={{ marginTop: 12 }}>Add rate scenario</Button>

      {/* Eligibility — the common core */}
      <Divider orientation="left" style={{ marginTop: 24 }}>Eligibility</Divider>
      <Row gutter={12}>
        <Col xs={12} md={5}>
          <Form.Item label="LTV max (%)" style={{ marginBottom: 12 }}>
            <InputNumber style={full} value={e.ltv_max ?? undefined} onChange={(v) => setElig('ltv_max', v as number | null)} min={0} max={100} />
          </Form.Item>
        </Col>
        <Col xs={12} md={5}>
          <Form.Item label="FICO min" style={{ marginBottom: 12 }}>
            <InputNumber style={full} value={e.fico_min ?? undefined} onChange={(v) => setElig('fico_min', v as number | null)} min={300} max={850} />
          </Form.Item>
        </Col>
        <Col xs={12} md={5}>
          <Form.Item label="DTI max (%)" style={{ marginBottom: 12 }}>
            <InputNumber style={full} value={e.dti_max ?? undefined} onChange={(v) => setElig('dti_max', v as number | null)} min={0} max={100} />
          </Form.Item>
        </Col>
        <Col xs={12} md={9}>
          <Form.Item label="Units" style={{ marginBottom: 12 }}>
            <Input value={e.units ?? ''} onChange={(ev) => setElig('units', ev.target.value || null)} placeholder="e.g. 1-4, Condos, PUD, Co-Ops" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col xs={24} md={12}>
          <Form.Item label="Loan amount min (USD)" style={{ marginBottom: 12 }}>
            <InputNumber style={full} value={e.loan_amount_min ?? undefined} onChange={(v) => setElig('loan_amount_min', v as number | null)} min={0} step={100000} {...usd} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="Loan amount max (USD)" style={{ marginBottom: 12 }}>
            <InputNumber style={full} value={e.loan_amount_max ?? undefined} onChange={(v) => setElig('loan_amount_max', v as number | null)} min={0} step={100000} {...usd} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col xs={24} md={12}>
          <Form.Item label="States included" style={{ marginBottom: 12 }}>
            <Select mode="multiple" style={full} value={e.states_included} onChange={(v) => setElig('states_included', v)}
              options={US_STATES.map((s) => ({ value: s, label: s }))} placeholder="Eligible states (empty = all)" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="States excluded" style={{ marginBottom: 12 }}>
            <Select mode="multiple" style={full} value={e.states_excluded} onChange={(v) => setElig('states_excluded', v)}
              options={US_STATES.map((s) => ({ value: s, label: s }))} placeholder="e.g. HI, AK, DC" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col xs={24} md={8}>
          <Form.Item label="Loan purposes" style={{ marginBottom: 12 }}>
            <OrderedListEditor value={e.loan_purposes} onChange={(v) => setElig('loan_purposes', v)} placeholder="Purchase, Rate/Term, Cash Out" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Occupancy" style={{ marginBottom: 12 }}>
            <OrderedListEditor value={e.occupancy} onChange={(v) => setElig('occupancy', v)} placeholder="Primary, Second home, Investment" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Property types" style={{ marginBottom: 12 }}>
            <OrderedListEditor value={e.property_types} onChange={(v) => setElig('property_types', v)} placeholder="SFR, Condo, 2-4 unit" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col xs={24} md={8}>
          <Form.Item label="Borrower types" style={{ marginBottom: 12 }}>
            <OrderedListEditor value={e.borrower_types} onChange={(v) => setElig('borrower_types', v)} placeholder="Foreign Nationals, Self-employed" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Documentation" style={{ marginBottom: 12 }}>
            <Input value={e.documentation ?? ''} onChange={(ev) => setElig('documentation', ev.target.value || null)} placeholder="e.g. Full doc, Bank statement, P&L" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Reserves" style={{ marginBottom: 12 }}>
            <Input value={e.reserves ?? ''} onChange={(ev) => setElig('reserves', ev.target.value || null)} placeholder="e.g. 6 months" />
          </Form.Item>
        </Col>
      </Row>

      {/* Highlights / features / notes */}
      <Divider orientation="left">Highlights &amp; features</Divider>
      <Form.Item label="Highlights" style={{ marginBottom: 12 }} help="Bullet selling points; press Enter to add each">
        <OrderedListEditor value={value.highlights} onChange={(v) => set('highlights', v)} placeholder="Add a highlight and press Enter" />
      </Form.Item>
      <Form.Item label="Special features" style={{ marginBottom: 12 }} help="Unusual/program-specific features">
        <OrderedListEditor value={value.special_features} onChange={(v) => set('special_features', v)} placeholder="Add a feature and press Enter" />
      </Form.Item>
      <Form.Item label="Notes" style={{ marginBottom: 0 }}>
        <Input.TextArea rows={4} value={value.notes ?? ''} onChange={(ev) => set('notes', ev.target.value || null)} placeholder="Free-text details / disclosures / anything program-specific" />
      </Form.Item>
    </Form>
  );
}

function ScenarioCard({ index, value, onChange, onRemove }: {
  index: number; value: RateScenario; onChange: (n: RateScenario) => void; onRemove: () => void;
}) {
  const set = <K extends keyof RateScenario>(k: K, v: RateScenario[K]) => onChange({ ...value, [k]: v });
  const setA = <K extends keyof RateAssumptions>(k: K, v: RateAssumptions[K]) =>
    onChange({ ...value, assumptions: { ...value.assumptions, [k]: v } });
  const a = value.assumptions;
  return (
    <Card
      size="small"
      title={<Input size="small" variant="borderless" value={value.label} onChange={(ev) => set('label', ev.target.value)} placeholder={`Scenario ${index + 1} label (e.g. Primary 75% LTV)`} style={{ fontWeight: 600 }} />}
      extra={<Button size="small" danger icon={<DeleteOutlined />} onClick={onRemove} />}
    >
      <Row gutter={12}>
        <Col xs={12} md={6}><Form.Item label="Loan type" style={{ marginBottom: 8 }}><Input value={value.loan_type} onChange={(ev) => set('loan_type', ev.target.value)} placeholder="7/6 ARM" /></Form.Item></Col>
        <Col xs={12} md={5}><Form.Item label="Rate (%)" style={{ marginBottom: 8 }}><InputNumber style={full} value={value.interest_rate ?? undefined} onChange={(v) => set('interest_rate', v as number | null)} step={0.01} /></Form.Item></Col>
        <Col xs={12} md={5}><Form.Item label="APR (%)" style={{ marginBottom: 8 }}><InputNumber style={full} value={value.apr ?? undefined} onChange={(v) => set('apr', v as number | null)} step={0.01} /></Form.Item></Col>
        <Col xs={12} md={4}><Form.Item label="Points" style={{ marginBottom: 8 }}><InputNumber style={full} value={value.points ?? undefined} onChange={(v) => set('points', v as number | null)} step={0.125} /></Form.Item></Col>
        <Col xs={12} md={4}><Form.Item label="As-of date" style={{ marginBottom: 8 }}><Input value={value.rate_as_of ?? ''} onChange={(ev) => set('rate_as_of', ev.target.value || null)} placeholder="YYYY-MM-DD" /></Form.Item></Col>
      </Row>
      <Row gutter={12}>
        <Col xs={8} md={8}><Form.Item label="Index" style={{ marginBottom: 8 }}><Input value={value.index ?? ''} onChange={(ev) => set('index', ev.target.value || null)} placeholder="SOFR 30 Day Avg" /></Form.Item></Col>
        <Col xs={8} md={8}><Form.Item label="Margin" style={{ marginBottom: 8 }}><Input value={value.margin ?? ''} onChange={(ev) => set('margin', ev.target.value || null)} placeholder="2.75" /></Form.Item></Col>
        <Col xs={8} md={8}><Form.Item label="Caps" style={{ marginBottom: 8 }}><Input value={value.caps ?? ''} onChange={(ev) => set('caps', ev.target.value || null)} placeholder="5/1/5" /></Form.Item></Col>
      </Row>
      <Divider orientation="left" plain style={{ margin: '4px 0 8px', fontSize: 12 }}>Assumptions</Divider>
      <Row gutter={12}>
        <Col xs={12} md={5}><Form.Item label="LTV (%)" style={{ marginBottom: 0 }}><InputNumber style={full} value={a.ltv ?? undefined} onChange={(v) => setA('ltv', v as number | null)} min={0} max={100} /></Form.Item></Col>
        <Col xs={12} md={7}><Form.Item label="Loan amount" style={{ marginBottom: 0 }}><InputNumber style={full} value={a.loan_amount ?? undefined} onChange={(v) => setA('loan_amount', v as number | null)} min={0} step={100000} {...usd} /></Form.Item></Col>
        <Col xs={12} md={4}><Form.Item label="FICO" style={{ marginBottom: 0 }}><InputNumber style={full} value={a.credit_score ?? undefined} onChange={(v) => setA('credit_score', v as number | null)} min={300} max={850} /></Form.Item></Col>
        <Col xs={12} md={4}><Form.Item label="Property" style={{ marginBottom: 0 }}><Input value={a.property_type ?? ''} onChange={(ev) => setA('property_type', ev.target.value || null)} placeholder="SFR" /></Form.Item></Col>
        <Col xs={12} md={4}><Form.Item label="DTI (%)" style={{ marginBottom: 0 }}><InputNumber style={full} value={a.dti ?? undefined} onChange={(v) => setA('dti', v as number | null)} min={0} max={100} /></Form.Item></Col>
      </Row>
      <Row gutter={12} style={{ marginTop: 8 }}>
        <Col xs={12} md={12}><Form.Item label="Transaction" style={{ marginBottom: 0 }}><Input value={a.transaction_type ?? ''} onChange={(ev) => setA('transaction_type', ev.target.value || null)} placeholder="Purchase" /></Form.Item></Col>
        <Col xs={12} md={12}><Form.Item label="Occupancy" style={{ marginBottom: 0 }}><Input value={a.occupancy ?? ''} onChange={(ev) => setA('occupancy', ev.target.value || null)} placeholder="Primary" /></Form.Item></Col>
      </Row>
    </Card>
  );
}
